import styled from 'styled-components';
import { ReactComponent as OriginalIcon } from '../../icons/back.svg';
import { ReactComponent as LaterArrivalIcon } from '../../icons/down_next_arrow.svg';
import { ReactComponent as EarlierArrivalIcon } from '../../icons/down_previous_arrow.svg';
import { ReactComponent as LaterDepartureIcon } from '../../icons/up_next_arrow.svg';
import { ReactComponent as EarlierDepartureIcon } from '../../icons/up_previous_arrow.svg';
import { BodySRegular } from '../TypographyStyles';
import { fontFamily, slateColors } from '../ContentPages/AppLandingPage/styles';
import { useTranslation } from 'react-i18next';

const AlternativeButton = styled('button')<{ $isLoading: boolean }>`
  font-family: ${fontFamily};
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: ${slateColors[600]};
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 0.5rem;
  transition: all 0.2s;
  leading-trim: both;
  text-edge: cap;
  -webkit-tap-highlight-color: transparent;

  svg {
    fill: currentColor;
    height: 0.75rem;
    width: 0.75rem;
    transition: fill 0.2s;
  }

  &:hover {
    color: ${slateColors[700]};
    background: ${slateColors[100]};
  }

  &:active {
    background: ${slateColors[200]};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${slateColors[200]};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${slateColors[400]};
    background: none;
    svg {
      fill: currentColor;
    }
  }
`;

type ButtonProps = {
    onClick: () => void;
    isLoading?: boolean;
};

// Earlier Arrival Button
export const EarlierArrivalButton: React.FC<ButtonProps> = ({ onClick, isLoading = false }) => {
  const { t } = useTranslation();
  return (
    <AlternativeButton onClick={onClick} $isLoading={isLoading} disabled={isLoading}>
      <EarlierArrivalIcon />
      <BodySRegular>{isLoading ? t('controls.alternativeButtons.searching') : t('controls.alternativeButtons.arriveEarlier')}</BodySRegular>
    </AlternativeButton>
  );
};

// Later Arrival Button
export const LaterArrivalButton: React.FC<ButtonProps> = ({ onClick, isLoading = false }) => {
  const { t } = useTranslation();
  return (
    <AlternativeButton onClick={onClick} $isLoading={isLoading} disabled={isLoading}>
      <LaterArrivalIcon />
      <BodySRegular>{isLoading ? t('controls.alternativeButtons.searching') : t('controls.alternativeButtons.arriveLater')}</BodySRegular>
    </AlternativeButton>
  );
};

// Earlier Departure Button
export const EarlierDepartureButton: React.FC<ButtonProps> = ({ onClick, isLoading = false }) => {
  const { t } = useTranslation();
  return (
    <AlternativeButton onClick={onClick} $isLoading={isLoading} disabled={isLoading}>
      <EarlierDepartureIcon />
      <BodySRegular>{isLoading ? t('controls.alternativeButtons.searching') : t('controls.alternativeButtons.departEarlier')}</BodySRegular>
    </AlternativeButton>
  );
};
  
// Later Departure Button
export const LaterDepartureButton: React.FC<ButtonProps> = ({ onClick, isLoading = false }) => {
  const { t } = useTranslation();
  return (
    <AlternativeButton onClick={onClick} $isLoading={isLoading} disabled={isLoading}>
      <LaterDepartureIcon />
      <BodySRegular>{isLoading ? t('controls.alternativeButtons.searching') : t('controls.alternativeButtons.departLater')}</BodySRegular>
    </AlternativeButton>
  );
};

// Original Journey Button
export const OriginalJourneyButton: React.FC<ButtonProps> = ({ onClick, isLoading = false }) => {
  const { t } = useTranslation();
  return (
    <AlternativeButton onClick={onClick} $isLoading={isLoading} disabled={isLoading}>
      <OriginalIcon />
      <BodySRegular>{t('controls.alternativeButtons.backToOriginalJourney')}</BodySRegular>
    </AlternativeButton>
  );
};